import { useEffect } from "preact/hooks";
import {
    ApproveEntityStatus,
    ApproveKind,
    Decimal,
} from "@kase/my-marathon-communication";
import {
    signal,
    useComputed,
    useSignal,
    useSignalEffect,
} from "@preact/signals";
import { ApproveExpense, ApproveInvoice, useApp } from "~/core";
import { dateFromApi, upsert } from "~/utils";
import { ApprovalWidgetView } from "../../../shared";
import { SingleWidgetProps } from "../interfaces";

const onlyOpen = (approval: ApproveExpense | ApproveInvoice) =>
    approval.status === ApproveEntityStatus.ApproveOpen &&
    (!approval.mine.peek() || approval.mine.peek()?.kind === ApproveKind.None);

const useWidget = ({}: SingleWidgetProps) => {
    const { api } = useApp();
    const apiFilter = useComputed(() => ({
        statuses: [ApproveEntityStatus.ApproveOpen],
        companies: api.company.approve.value.map((c) => c.code),
    }));
    const loading = useComputed(
        () => api.approve.loading(apiFilter.value).value,
    );
    const approvals = useSignal<ApprovalWidgetView[]>([]);

    useEffect(() => {
        const viewInvoice = (invoice: ApproveInvoice): ApprovalWidgetView => ({
            id: invoice.id,
            type: "invoice",
            dueDate: dateFromApi(invoice.due_date),
            supplier: api.supplier.get(invoice.supplier),
            employee: null,
            amount: Decimal.from(invoice.amount).toNumber(),
            company: invoice.company,
            raw: invoice,
            tag: signal(0),
        });

        const viewExpense = (expense: ApproveExpense): ApprovalWidgetView => ({
            id: expense.id,
            type: "expense",
            dueDate: expense.date ? dateFromApi(expense.date) : null,
            supplier: null,
            employee: api.employee.get(expense.employee),
            amount: Decimal.from(expense.amount).toNumber(),
            company: expense.company,
            raw: expense,
            tag: signal(0),
        });

        const handleAddInvoice = (invoices: ApproveInvoice[]) => {
            const newItems = invoices.filter(onlyOpen);

            if (newItems.length > 0) {
                approvals.value = upsert(
                    approvals.peek().slice(),
                    newItems.map(viewInvoice),
                    "id",
                );
            }
        };

        const handleAddExpense = (expenses: ApproveExpense[]) => {
            const newItems = expenses.filter(onlyOpen);

            if (newItems.length > 0) {
                approvals.value = upsert(
                    approvals.peek().slice(),
                    newItems.map(viewExpense),
                    "id",
                );
            }
        };

        const handleDelete = (items: (ApproveExpense | ApproveInvoice)[]) => {
            approvals.value = approvals
                .peek()
                .filter((a) => !items.find((i) => i.id === a.id));
        };

        const handleRelatedUpdate = () => {
            for (const approval of approvals.peek()) {
                if (approval.type === "expense" && !approval.employee) {
                    const employee = api.employee.get(approval.raw.employee);

                    if (employee) {
                        approval.employee = employee;
                        approval.tag.value = approval.tag.peek() + 1;
                    }
                } else if (approval.type === "invoice" && !approval.supplier) {
                    const supplier = api.supplier.get(approval.raw.supplier);

                    if (supplier) {
                        approval.supplier = supplier;
                        approval.tag.value = approval.tag.peek() + 1;
                    }
                }
            }
        };

        const handleAddMine = () => {
            const newItems = approvals
                .peek()
                .filter((item) => onlyOpen(item.raw));

            if (newItems.length !== approvals.peek().length) {
                approvals.value = newItems;
            }
        };

        const handleRemoveMine = () => {
            const newInvoices = api.approve.invoices.peek().filter(onlyOpen);
            const newExpenses = api.approve.expenses.peek().filter(onlyOpen);
            const newLength = newInvoices.length + newExpenses.length;

            if (newLength !== approvals.peek().length) {
                approvals.value = [
                    ...newInvoices.map(viewInvoice),
                    ...newExpenses.map(viewExpense),
                ];
            }
        };

        api.approve.approval.on("add", handleAddMine);
        api.approve.approval.on("remove", handleRemoveMine);
        api.approve.onInvoice("add", handleAddInvoice);
        api.approve.onInvoice("remove", handleDelete);
        api.approve.onExpense("add", handleAddExpense);
        api.approve.onExpense("remove", handleDelete);
        api.supplier.on("add", handleRelatedUpdate);
        api.employee.on("add", handleRelatedUpdate);

        handleAddInvoice(api.approve.invoices.peek());
        handleAddExpense(api.approve.expenses.peek());

        return () => {
            api.approve.approval.off("add", handleAddMine);
            api.approve.approval.off("remove", handleRemoveMine);
            api.approve.offInvoice("add", handleAddInvoice);
            api.approve.offInvoice("remove", handleDelete);
            api.approve.offExpense("add", handleAddExpense);
            api.approve.offExpense("remove", handleDelete);
            api.supplier.off("add", handleRelatedUpdate);
            api.employee.off("add", handleRelatedUpdate);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        api.supplier.listen();
    }, [api]);

    useSignalEffect(() => {
        api.approve.listen(apiFilter.value);
    });

    return { loading, approvals };
};

export default useWidget;
